<template>
  <div class="trust-email-dashboard-page">
    <div
      v-if="!isMaestro() && !isLiteView()"
      class="progress-container"
    >
      <div class="p-medium">
        <p
          class="tertiary-link top-margin-zero"
        >
          <a
            ref="portfolioSummaryLink"
            href="#"
            @click.prevent="handleRedirectButtonClick()"
          >
            <pvd-icon
              icon="arrow"
              class="color-action "
            />
            {{ redirectButtonValue }}

          </a>
        </p>
      </div>
    </div>

    <div class="pvd-container">
      <div class="trust-email-dashboard-page-header">
        <h2 class="pvd-heading-XL">
          Next steps
        </h2>

        <p v-if="signerStatus.signers<=1">
          Trust Account
        </p>
      </div>

      <pvd-system-message
        v-if="isDeclinedStatus"
        class="identity-error-message"
        placement="page"
        type="warning"
        dismissible="false"
        message="One of the trustees has declined to sign. To proceed, you'll need to start a new application."
      />

      <pvd-system-message
        v-if="!isDeclinedStatus && !isCompletedStatus"
        class="identity-error-message"
        placement="page"
        type="warning"
        dismissible="false"
        message="Just one more step. Once all signatures are received, you'll be notified to finish opening your account on this page."
      />
    </div>
    <form
      novalidate="novalidate"
      @submit.prevent
    >
      <div class="pvd-container trust-email-dashboard-page-card">
        <div class="pvd-card">
          <h4 class="pvd-heading-M">
            Track signatures
          </h4>
          <p v-if="!isCompletedStatus">
            To actively use this account, you must have the signatures of all trustees listed below.
          </p>
          <p v-if="isCompletedStatus">
            All trustee signatures have been received and you can now complete the account opening process.
          </p>
          <div class="table-container">
            <table
              class="pvd-table-simple trust-signature-status-table"
            >
              <thead>
                <tr>
                  <th class="text-left">
                    Name
                  </th>
                  <th class="text-left">
                    Email
                  </th>
                  <th class="text-left">
                    Signature status
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(signer, index) in signerStatus.signers"
                  :key="index"
                >
                  <td class="text-left name-column">
                    {{ signer.firstName }} {{ signer.lastName }}
                  </td>
                  <td class="text-left email-column">
                    <div>
                      {{ signer.email }}
                    </div>
                  </td>
                  <td
                    class="text-left status-column"
                  >
                    <div
                      v-if="signer.signerStatus === 'SIGNER_AGREED'"
                      class="signature-status"
                    >
                      <pvd-icon
                        icon="confirmation"
                        class="color-brand"
                      />
                      Signed {{ formattedDate(signer.modifiedAt) }}
                    </div>
                    <div
                      v-if="signer.signerStatus === 'SIGNER_PENDING'"
                      class="signature-status"
                    >
                      Pending
                    </div>
                    <div
                      v-if="signer.signerStatus === 'SIGNER_DECLINED'"
                      class="signature-status"
                      style="color:#E01616"
                    >
                      <pvd-icon
                        icon="alert"
                      />
                      Declined
                    </div>
                  </td>
                  <td class="text-right operation-column">
                    <div
                      v-if="signer.signerStatus === 'SIGNER_PENDING' && !signer.reminderSent"
                    >
                      <button
                        class="pvd-btn btn-ghost-action"
                        type="button"
                        @click="handleSendEmail(signer, index)"
                      >
                        <pvd-loading-spinner
                          v-if="signer.showSpinner"
                          :active="signer.showSpinner"
                          type="action"
                          inline="true"
                        />
                        Send reminder
                      </button>
                    </div>
                    <div
                      v-if="reminderSentDate && !signer.emailEdited && signer.reminderSent
                        && (signer.signerStatus !== 'SIGNER_AGREED')&& (signer.signerStatus !== 'SIGNER_DECLINED')"
                      class="formatted-date"
                    >
                      Reminder sent {{ formattedDate(reminderSentDate) }}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <InlineMessage
            v-if="showIncompletedError && !isCompletedStatus"
            id="incompleted-error-"
            message="All signatures must be received before you can finish opening your account."
          />
          <div class="operation-button-group">
            <button
              id="start-new-application-modal-trigger"
              class="pvd-btn btn-ghost-action start-new-button"
              type="button"
            >
              Start a new trust account application
            </button>
            <button
              v-if="!isDeclinedStatus"
              id="primaryAction"
              type="button"
              :disabled="shouldDisableButton === 'Y'"
              class="pvd-btn btn-action loading-button finish-account-opening-button"
              @click.prevent="accountOpeningButtonClicked"
            >
              <pvd-loading-spinner
                v-if="shouldShowLoadingSpinner === 'Y'"
                :active="shouldShowLoadingSpinner === 'Y'"
                type="white"
                inline="true"
              />
              {{ buttonLabel }}
            </button>
            <pvd-modal
              id="start-new-application-modal"
              for="start-new-application-modal-trigger"
              class="small text-left"
            >
              <div slot="modal-header">
                <h2 class="pvd-heading-L">
                  Are you sure you want to start a new application?
                </h2>
              </div>
              <div slot="modal-body">
                <div class="pvd-container">
                  <p>
                    Starting a new trust application will delete the current one. If you'd like to stay with the current application,
                    please wait until your account has been opened before starting a new one.
                  </p>
                </div>
              </div>
              <div slot="modal-footer">
                <div class="pvd-btn-row">
                  <div class="button-group">
                    <button
                      class="pvd-btn btn-action"
                      style="display:inline; white-space:nowrap"
                      nowrap
                      @click.prevent="startNewButtonClicked()"
                    >
                      <pvd-loading-spinner
                        v-if="shouldShowModalBtnLoadingSpinner"
                        :active="shouldShowModalBtnLoadingSpinner"
                        type="white"
                        inline="true"
                      />
                      Start new application
                    </button>
                    <button
                      class="pvd-btn btn-secondary"
                      @click.prevent="exitStartNewModal()"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </pvd-modal>
          </div>
        </div>
      </div>
    </form>
    <div
      v-if="!isMaestro() && !isLiteView()"
      class="pvd-container trust-email-dashboard-page-redirection"
    >
      <p
        class="tertiary-link text-right"
      >
        <a
          ref="portfolioSummaryLink"
          href="#"
          @click.prevent="handleRedirectButtonClick()"
        >
          {{ redirectButtonValue }}
          <pvd-icon
            icon="arrow"
            class="color-action "
          />
        </a>
      </p>
      <p
        v-if="uc_isCustomer"
        class="text-right "
      >
        View and manage your accounts
      </p>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import dataHelper from '../mixins/dataHelper';
import utility from '../mixins/utility';
import validationHelper from '../mixins/validationHelper';
import measurement from '../mixins/measurement';
import pageNavigationHelper from '../mixins/pageNavigationHelper';
import InlineMessage from '../components/InlineMessage.vue';

export default {
  name: 'TrustEmailDashboardEcta',
  components: { InlineMessage },
  mixins: [dataHelper, utility, validationHelper, measurement, pageNavigationHelper],
  inject: ['$validator'],
  data() {
    return {
      signerStatus: {},
      reminderSentDate: '',
      isCompletedStatus: false,
      timer: null,
      isLoading: false,
      isModalBtnLoading: false,
      pageTitle: 'Next Steps',
      showIncompletedError: false,
    };
  },
  computed: {
    redirectButtonValue() {
      if (this.uc_isCustomer) {
        return ' Go to your portfolio summary ';
      }
      return ' Return to Fidelity.com ';
    },
    shouldDisableButton() {
      return this.isLoading || this.uc_isRep ? 'Y' : 'N';
    },
    shouldShowLoadingSpinner() {
      return this.isLoading ? 'Y' : 'N';
    },
    buttonLabel() {
      return this.isLoading ? 'Please Wait' : 'Finish opening account';
    },
    shouldShowModalBtnLoadingSpinner() {
      return this.isModalBtnLoading;
    },
    isDeclinedStatus() {
      return this.signerStatus?.signers?.some((signer) => signer.signerStatus === 'SIGNER_DECLINED') || false;
    },
  },
  mounted() {
    this.isLoading = false;
    this.isModalBtnLoading = false;

    this.signerStatus = this.getInstanceData().formData.ectaSignInfo;
    this.timer = setInterval(this.setSignatureStatus, 15000);
    document.title = this.setTitle(this.pageTitle);
    document.body.style.backgroundColor = '#f2f2f2';
    this.isCompletedStatus = this.signerStatus?.status === 'CEREMONY_AGREED';

    try {
      const { storageInfo } = this.getInstanceData();
      if (storageInfo && storageInfo.recordId) {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('recordId', storageInfo.recordId);
        window.history.replaceState('TrustEmailDashboardEcta', '', `${window.location.pathname}?${currentUrl.searchParams.toString()}#/retrieve`);
      } else {
        window.history.replaceState('TrustEmailDashboardEcta', '', `${window.location.pathname}${window.location.search}`);
      }
    } catch (error) {
      // Ignore errors
    }
  },
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    document.body.style.backgroundColor = '#fff';
  },
  methods: {
    handleRedirectButtonClick() {
      const { portfolioSummaryUrl } = this.pd_envConfig;
      const fidComUrl = 'https://www.fidelity.com';
      if (this.uc_isCustomer) {
        window.location.href = portfolioSummaryUrl;
      } else {
        window.location.href = fidComUrl;
      }
    },
    formattedDate(dateString) {
      return this.formatSigStatusDate(dateString);
    },
    handleSendEmail(signer, index) {
      this.signerStatus.signers.splice(index, 1, { ...signer, showSpinner: true });
      const instanceData = this.getInstanceData();
      instanceData.sendEmailReminder = true;

      const that = this;

      axios.post('api/ecta/signature-status', { instanceData })
        .then((response) => {
          const updatedSignerstatus = response.data;
          if (updatedSignerstatus && updatedSignerstatus.signers) {
            that.signerStatus.signers.splice(index, 1, { ...signer, showSpinner: false, reminderSent: true });
            that.reminderSentDate = new Date();
          } else {
            that.signerStatus.signers.splice(index, 1, { ...signer, showSpinner: false });
          }
        }).catch(() => {});
    },
    accountOpeningButtonClicked() {
      if (!this.isCompletedStatus) {
        this.showIncompletedError = true;
        return;
      }
      this.isLoading = true;
      this.performNavigation('finishOpeningAccount');
    },
    setSignatureStatus() {
      const instanceData = this.getInstanceData();
      const that = this;

      axios.post('api/ecta/signature-status', { instanceData })
        .then((response) => {
          const updatedSignerstatus = response.data;
          if (updatedSignerstatus && updatedSignerstatus.signers) {
            that.signerStatus.signers.forEach((signer) => {
              const updatedsigner = updatedSignerstatus.signers.find((person) => person.email === signer.email);
              // eslint-disable-next-line no-param-reassign
              signer.signerStatus = updatedsigner.signerStatus;
              // eslint-disable-next-line no-param-reassign
              signer.modifiedAt = updatedsigner.modifiedAt;
            });
            if (updatedSignerstatus.status === 'CEREMONY_AGREED') {
              that.isCompletedStatus = true;
              clearInterval(that.timer);
              that.timer = null;
            }
            // If any signer has declined, stop polling — the ceremony can no longer be completed
            const hasDeclined = updatedSignerstatus.signers.some((s) => s.signerStatus === 'SIGNER_DECLINED');
            if (hasDeclined) {
              clearInterval(that.timer);
              that.timer = null;
            }
          }
          return {};
        }).catch(() => {});
    },
    exitStartNewModal() {
      document
        .getElementById('start-new-application-modal')
        .setAttribute('active', 'false');
    },
    startNewButtonClicked() {
      this.isModalBtnLoading = true;
      this.performNavigation('startNewApplication');
    },
  },
};
</script>

<style scoped>
  .p-medium pvd-icon {
    transform:rotateY(-180deg);
    vertical-align: text-top;
  }

  .p-medium .tertiary-link{
    font-size: 1em;
  }

  .trust-email-dashboard-page-header{
    display: -ms-flexbox;
    display: flex;
    -ms-flex-preferred-size: 100%;
    flex-basis: 100%;
    -ms-flex-pack: justify;
    justify-content: space-between;
    -ms-flex-align: center;
    align-items: center;
  }

  .trust-email-dashboard-page-header p {
    font-weight: bold;
  }

  .trust-email-dashboard-page-card {
    padding-top: 0;
  }

  .pvd-card {
    margin: 0;
  }
  .pvd-card p {
    margin-bottom: .5em;
  }

  h2,h4 {
    font-weight: 300;
  }
  .table-container {
    overflow-x: auto;
  }

  .trust-signature-status-table {
    width: 100%;
    border-collapse: collapse;
    margin: 1.5em auto;
  }
  .trust-signature-status-table tr {
    border-bottom: 1px solid #CCCCCC;
    height: 70px;
  }

  .trust-signature-status-table th,td {
    padding: 1em;
    padding-left: 0;
  }

  .trust-signature-status-table tr td:first-child {
    width: 25%;
  }

  .trust-signature-status-table tr td:nth-child(2) {
    width: 25%;
  }

  .trust-signature-status-table .text-right {
    padding-right: 2px;
  }

  .trust-signature-status-table button, .formatted-date, .name-column, .email-column, .status-column {
    white-space: nowrap;
  }

  pvd-system-message {
    padding-top: .5em;
  }

  .trust-email-dashboard-page-redirection {
    padding-top: 0;
  }

  .trust-email-dashboard-page-redirection p{
    font-size: 1em;
    margin-top: .5em
  }

  .trust-email-dashboard-page-redirection .tertiary-link{
    margin-top: 0;
    font-weight: bold;
  }

  .start-new-button{
    margin-right:.5em;
    margin-bottom: 1em;
  }

  .finish-account-opening-button {
    margin-right: .5em;
    margin-bottom: 1em;
  }

  @media screen and (min-width: 40em) {
    .start-new-button{
      margin-bottom: 0em;
    }
    .finish-account-opening-button {
      margin-bottom: 0em;
    }
  }

  .cancel-edit-button{
    margin-right: .5em;
  }

  .operation-button-group {
    margin-top: 18px;
  }

  @media screen and (max-width: 40em) {
    .operation-button-group{
      display: -webkit-flex;
      display: flex;
      -webkit-align-items: center;
      align-items: center;
      -webkit-justify-content: center;
      justify-content: center;
      flex-direction: column;
    }
    .finish-account-opening-button, .start-new-button{
      width: 100%;
    }
  }

</style>
